import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Crown, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Royal Referral panel — shown on both Customer & Provider dashboards.
 * Displays the user's personal SMART-XXXXX code, current SMART Coin balance
 * (and JMD value), referral count, and a one-click share/copy CTA.
 */
export default function RoyalReferralCard({ compact = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        setLoading(true);
        api
            .get("/referrals/me")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    const copyCode = async () => {
        if (!data?.referral_code) return;
        try {
            await navigator.clipboard.writeText(data.referral_code);
            toast.success("Royal code copied to clipboard");
        } catch (e) {
            toast.error("Couldn't copy — try selecting and copying manually.");
        }
    };

    const shareLink = () => {
        if (!data?.referral_code) return;
        const url = `${window.location.origin}/?ref=${data.referral_code}`;
        const text = `I'm using SMART Jamaica for cleaning, beauty, babysitting + more — use my code ${data.referral_code} for a Royal welcome bonus.`;
        if (navigator.share) {
            navigator
                .share({ title: "SMART Jamaica · Royal Invite", text, url })
                .catch(() => {});
        } else {
            navigator.clipboard?.writeText(`${text}\n${url}`).then(
                () => toast.success("Royal invite copied — paste anywhere."),
                () => toast.error("Couldn't copy invite link.")
            );
        }
    };

    if (loading || !data) {
        return (
            <div
                className="bg-card border border-white/10 p-7 lg:p-9 rounded-sm relative overflow-hidden"
                data-testid="royal-referrals-loading"
            >
                <div className="overline mb-3">Royal Referrals</div>
                <div className="text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }

    return (
        <div
            className="bg-card border border-primary/30 p-7 lg:p-9 rounded-sm relative overflow-hidden"
            data-testid="royal-referrals-card"
        >
            {/* Gold halo */}
            <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-primary/8 blur-3xl pointer-events-none" />

            <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                    <Crown size={14} className="gold-text" strokeWidth={1.5} />
                    <div className="overline">Royal Referrals</div>
                </div>

                <h3 className="font-serif text-2xl sm:text-3xl leading-tight">
                    Earn <span className="italic gold-grad">SMART Coins</span> for every Royal invite.
                </h3>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                    {data.reward_per_referral.toLocaleString()} coins ({(data.reward_per_referral * data.coin_to_jmd_ratio).toFixed(0)} JMD) per friend who joins with your code.
                </p>

                {/* Code + balance grid */}
                <div className="mt-6 grid sm:grid-cols-2 gap-px bg-white/5 rounded-sm overflow-hidden">
                    <div className="bg-background/60 p-5">
                        <div className="overline mb-2">Your code</div>
                        <div className="flex items-center gap-3">
                            <code
                                className="font-mono text-2xl tracking-[0.2em] gold-text"
                                data-testid="royal-referral-code"
                            >
                                {data.referral_code}
                            </code>
                            <button
                                type="button"
                                onClick={copyCode}
                                className="text-muted-foreground hover:gold-text transition-colors"
                                aria-label="Copy code"
                                data-testid="copy-referral-code"
                            >
                                <Copy size={14} strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                    <div className="bg-background/60 p-5">
                        <div className="overline mb-2">Coin balance</div>
                        <div className="font-serif text-3xl tracking-tight" data-testid="royal-coin-balance">
                            <Sparkles
                                size={14}
                                className="inline-block gold-text mr-1.5 -mt-1"
                                strokeWidth={1.5}
                            />
                            {data.smart_coins.toLocaleString()}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                            ≈ ${data.smart_coin_value_jmd.toLocaleString()} JMD
                        </div>
                    </div>
                </div>

                {/* CTA + count */}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Button
                        onClick={shareLink}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-10 px-5"
                        data-testid="share-referral-btn"
                    >
                        Share Royal Invite
                    </Button>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Users size={12} strokeWidth={1.5} />
                        <span data-testid="referral-count">
                            {data.total_referrals} friend{data.total_referrals === 1 ? "" : "s"} joined
                        </span>
                    </div>
                </div>

                {!compact && data.recent_referrals?.length > 0 && (
                    <div className="mt-6 pt-5 border-t border-white/10">
                        <div className="overline mb-3">Recent royal joins</div>
                        <ul className="space-y-2">
                            {data.recent_referrals.slice(0, 5).map((r) => (
                                <li
                                    key={r.id}
                                    className="flex items-center justify-between text-xs"
                                >
                                    <span className="text-foreground/90">
                                        {r.referred_user_name}
                                    </span>
                                    <span className="gold-text font-mono">
                                        +{r.coins_awarded.toLocaleString()} coins
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <p className="mt-5 text-[10px] uppercase tracking-[0.25em] text-muted-foreground/70">
                    Every successful Royal invite = $100 JMD in coins · redeemable on bookings (v1.1)
                </p>
            </div>
        </div>
    );
}
